import { useCallback, useState, useEffect } from 'react';
import { motion } from 'motion/react';

let lerp = (t: number, outMin: number, outMax: number) =>
  t * (outMax - outMin) + outMin;

// --- Bubble config (backdrop behind score) ---

let bubbleColor = '#b95b13';
let bubbleBaseSizePx = 42;
let bubbleCount = 4;
let bubbleMinSizePx = 20;
let bubbleMaxSizePx = 40;
let bubbleSpreadXPx = 28; // max x offset from center
let bubbleSpreadYPx = 16; // max y offset from center
let bubbleStaggerS = 0.03; // seconds between each bubble appearing

let generateBubbles = () =>
  [...Array(bubbleCount)].map((_, i) => ({
    x: (Math.random() - 0.5) * 2 * bubbleSpreadXPx,
    y: (Math.random() - 0.5) * 2 * bubbleSpreadYPx,
    size: bubbleMinSizePx + Math.random() * (bubbleMaxSizePx - bubbleMinSizePx),
    color: bubbleColor,
    delay: i * bubbleStaggerS,
  }));

// --- Particle config ---

let particleColors = ['#da7427', '#f19939', '#ff8e24', '#e8963a', '#ffb347'];
let particleCount = 14;
let particleMinDistancePx = 40;
let particleMaxDistancePx = 100;
let particleMinSizePx = 5;
let particleMaxSizePx = 12;
let particleMinOutScale = 1;
let particleMaxOutScale = 1.8;
let particleAngleJitterRad = 0.6; // radians, ~34° total spread

// --- Timing ---

let burstDelayMs = 80;
let particleMinHoldMs = 1550;
let particleMaxHoldMs = 1620;
let particleHoldEase = 0.08; // index-based easing factor on hold time
let scoreVisibleMs = 1800;
let clearDelayMs = scoreVisibleMs + 800;
let vibrationMinScore = 20;
let vibrationMs = 200;

// --- Springs ---

let particleDriftMultiplier = 1.25; // drift target is further than burst position
let particleDriftDurationMs = 1800; // long ease so particles are still moving when recalled
let particleDriftEase: [number, number, number, number] = [0.08, 0.85, 0.3, 1]; // explosive burst, slow drift
let particleReturnSpring = { type: 'spring' as const, stiffness: 180 };
let particleReturnMinDamping = 12;
let particleReturnMaxDamping = 38;

let bubbleEnterSpring = { type: 'spring' as const, stiffness: 300, damping: 15 };
let bubbleExitTransition = { duration: 0.15 };
let fadeSpring = { type: 'spring' as const, stiffness: 210, damping: 20 };
let enterScale = 0.3;
let exitScale = 0.5;

let boopMs = 150;
let boopScale = 1.1;
let boopRotationDeg = 10;
let boopSpring = { type: 'spring' as const, stiffness: 300, damping: 12 };

// --- Precomputed particles ---

let generateParticles = () =>
  [...Array(particleCount)].map((_, i) => {
    let baseAngle = (i / particleCount) * Math.PI * 2 - Math.PI / 2;
    let angle = baseAngle + (Math.random() - 0.5) * particleAngleJitterRad;
    let distance =
      particleMinDistancePx +
      Math.random() * (particleMaxDistancePx - particleMinDistancePx);
    let t = i / (particleCount - 1);
    let x = Math.cos(angle) * distance;
    let y = Math.sin(angle) * distance;
    return {
      x,
      y,
      driftX: x * particleDriftMultiplier,
      driftY: y * particleDriftMultiplier,
      color: particleColors[Math.floor(Math.random() * particleColors.length)]!,
      size:
        particleMinSizePx +
        Math.random() * (particleMaxSizePx - particleMinSizePx),
      holdTime:
        lerp(t, particleMinHoldMs, particleMaxHoldMs) *
        (1 + t * particleHoldEase),
      returnDamping: lerp(
        t,
        particleReturnMinDamping,
        particleReturnMaxDamping,
      ),
      outScale:
        particleMinOutScale +
        Math.random() * (particleMaxOutScale - particleMinOutScale),
    };
  });

let useBoop = ({ timing }: { timing: number }): [boolean, () => void] => {
  let [isBooped, setIsBooped] = useState(false);
  let trigger = useCallback(() => {
    setIsBooped(true);
  }, []);

  useEffect(() => {
    if (!isBooped) return;
    let timeoutId = window.setTimeout(() => {
      setIsBooped(false);
    }, timing);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isBooped, timing]);

  return [isBooped, trigger];
};

let ScoreAlert = ({
  score,
  onClear,
}: {
  score?: number;
  onClear: () => void;
}) => {
  let [isUp, setIsUp] = useState(false);
  let [particles, setParticles] = useState(generateParticles);
  let [bubbles, setBubbles] = useState(generateBubbles);
  let [particlesOut, setParticlesOut] = useState<boolean[]>(
    () => Array.from({ length: particleCount }, () => false),
  );
  let [isBooped, textTrigger] = useBoop({ timing: boopMs });

  useEffect(() => {
    if (score === undefined) return;
    setParticles(generateParticles());
    setBubbles(generateBubbles());
    setIsUp(true);
    let downTimeout = window.setTimeout(() => {
      setIsUp(false);
    }, scoreVisibleMs);
    let onClearTimeout = window.setTimeout(() => {
      onClear();
    }, clearDelayMs);

    return () => {
      window.clearTimeout(downTimeout);
      window.clearTimeout(onClearTimeout);
      setIsUp(false);
      onClear();
    };
  }, [score, onClear]);

  useEffect(() => {
    if (!isUp) return;
    textTrigger();

    let burstTimeout = window.setTimeout(() => {
      setParticlesOut(Array.from({ length: particleCount }, () => true));
    }, burstDelayMs);

    let returnTimeouts = particles.map((p, i) =>
      window.setTimeout(() => {
        setParticlesOut((prev) => {
          let next = [...prev];
          next[i] = false;
          return next;
        });
      }, burstDelayMs + p.holdTime),
    );

    return () => {
      window.clearTimeout(burstTimeout);
      returnTimeouts.forEach((t) => window.clearTimeout(t));
    };
  }, [isUp, textTrigger]);

  useEffect(() => {
    if (!isUp) return;
    if (score === undefined) return;
    if (score < vibrationMinScore) return;
    navigator.vibrate?.(vibrationMs);
  }, [isUp, score]);

  if (score === undefined) return null;

  return (
    <div className="absolute top-1/2 left-1/2 z-50 -translate-x-1/2 -translate-y-1/2 transform">
      <motion.div
        initial={{ opacity: 0, scale: enterScale }}
        animate={
          isUp ? { opacity: 1, scale: 1 } : { opacity: 0, scale: exitScale }
        }
        transition={fadeSpring}
      >
        <div className="relative">
          <motion.div
            className="absolute top-1/2 left-1/2 rounded-full"
            style={{
              width: bubbleBaseSizePx,
              height: bubbleBaseSizePx,
              backgroundColor: bubbleColor,
              marginLeft: -bubbleBaseSizePx / 2,
              marginTop: -bubbleBaseSizePx / 2,
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={
              isUp
                ? {
                    scale: 1,
                    opacity: 1,
                    transition: bubbleEnterSpring,
                  }
                : { scale: 0, opacity: 0, transition: bubbleExitTransition }
            }
          />
          {bubbles.map((b, i) => (
            <motion.div
              key={i}
              className="absolute top-1/2 left-1/2 rounded-full"
              style={{
                width: b.size,
                height: b.size,
                backgroundColor: b.color,
                marginLeft: -b.size / 2,
                marginTop: -b.size / 2,
              }}
              initial={{ scale: 0, opacity: 0 }}
              animate={
                isUp
                  ? {
                      x: b.x,
                      y: b.y,
                      scale: 1,
                      opacity: 1,
                      transition: {
                        ...bubbleEnterSpring,
                        delay: b.delay,
                      },
                    }
                  : {
                      scale: 0,
                      opacity: 0,
                      transition: bubbleExitTransition,
                    }
              }
            />
          ))}
          <motion.div
            className="relative text-4xl font-semibold"
            animate={{
              scale: isBooped ? boopScale : 1,
              rotate: isBooped ? boopRotationDeg : 0,
            }}
            transition={boopSpring}
          >
            +{score}
          </motion.div>
        </div>
        {particles.map((p, i) => (
          <motion.div
            key={i}
            className="absolute top-1/2 left-1/2 rounded-full"
            style={{
              width: p.size,
              height: p.size,
              backgroundColor: p.color,
            }}
            variants={{
              out: {
                x: p.driftX,
                y: p.driftY,
                scale: p.outScale,
                opacity: 1,
                transition: {
                  type: 'tween',
                  duration: particleDriftDurationMs / 1000,
                  ease: particleDriftEase,
                },
              },
              rest: {
                x: 0,
                y: 0,
                scale: 0,
                opacity: 0,
                transition: {
                  ...particleReturnSpring,
                  damping: p.returnDamping,
                },
              },
            }}
            animate={particlesOut[i] ? 'out' : 'rest'}
          />
        ))}
      </motion.div>
    </div>
  );
};

export default ScoreAlert;
